import { Dialog, DialogTitle, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { AssistantAction } from 'src/sections/@dashboard/visit/utils/assistantActions';
import { markdownToEditorHtml } from 'src/utils/markdownToEditorHtml';
import { translateKey } from 'src/locales/translate';
import {
  VISIT_MODAL_PAPER_QUERY,
  VISIT_MODAL_PAPER_MEDIA,
  VISIT_MODAL_ASSISTANT_QUERY,
} from 'src/sections/@dashboard/organization/components/OrganizationVisitModal/visitModalLayout';
import PatientNoteModalBody from './PatientNoteModalBody';

export type PatientNoteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialContent?: string;
  profileId: string;
  /** Guest visits have no profile — the assistant is scoped by visit instead. */
  visitId?: string;
  patientName?: string;
  onSubmit: (content: string) => Promise<void>;
  isSaving: boolean;
  /** Whether the AI assistant should be available in this modal. Pass false for
   *  non-org surfaces (e.g. the pet-owner files page) or for org admins. */
  canUseAssistant: boolean;
  /** Whether the voice-dictation mic should be enabled inside the editor.
   *  Pass false on surfaces where OrganizationProvider is not mounted. */
  enableDictation: boolean;
  /** Open the note view-only (locked editor, single Close button, no assistant).
   *  Used when the note's edit window has closed (e.g. a visit completed more
   *  than 30 days ago). */
  readOnly?: boolean;
};

export default function PatientNoteModal({
  isOpen,
  onClose,
  mode,
  initialContent = '',
  profileId,
  visitId,
  patientName = '',
  onSubmit,
  isSaving,
  canUseAssistant,
  enableDictation,
  readOnly = false,
}: PatientNoteModalProps) {
  const isPaperScroll = useMediaQuery(VISIT_MODAL_PAPER_QUERY);
  const assistantFits = useMediaQuery(VISIT_MODAL_ASSISTANT_QUERY);
  const [content, setContent] = useState(initialContent);

  // Reset the editor whenever the modal (re)opens or targets a different note.
  useEffect(() => {
    if (isOpen) setContent(initialContent);
  }, [isOpen, initialContent]);

  const handleAssistantAction = useCallback((action: AssistantAction) => {
    if (action.type === 'fill_text_note') {
      setContent(markdownToEditorHtml(action.payload.notes));
    }
  }, []);

  // Callers supply canUseAssistant: org professionals get the AI assistant
  // (side-by-side when the viewport is wide enough, otherwise as a second tab),
  // org admins and non-org surfaces get the plain editor only. View-only notes
  // never show the assistant — they're a read surface, not a workspace.
  const sideBySide = canUseAssistant && assistantFits && !readOnly;
  const layout = readOnly || !canUseAssistant
    ? 'editor-only'
    : assistantFits
      ? 'side-by-side'
      : 'tabs';

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      scroll={isPaperScroll ? 'paper' : 'body'}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
          maxWidth: sideBySide ? '1560px' : '900px',
          mx: 0,
          my: { xs: 2, sm: 4 },
          minHeight: { md: 750 },
          [VISIT_MODAL_PAPER_MEDIA]: {
            maxHeight: 'calc(100vh - 64px)',
            height: 'min(1050px, calc(100vh - 64px))',
          },
        },
      }}
    >
      {/* Only the header is shared across the full width; the editor and the
          assistant chat sit side by side beneath it (see PatientNoteModalBody). */}
      <DialogTitle
        component="div"
        sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {readOnly
          ? translateKey('hardcoded.notatka_tekstowa')
          : mode === 'create'
            ? translateKey('hardcoded.nowa_notatka_tekstowa')
            : translateKey('hardcoded.edytuj_notatke_tekstowa')}
      </DialogTitle>
      <PatientNoteModalBody
        content={content}
        onContentChange={setContent}
        layout={layout}
        isPaperScroll={isPaperScroll}
        profileId={profileId}
        visitId={visitId}
        patientName={patientName}
        onAssistantAction={handleAssistantAction}
        onClose={onClose}
        onSubmit={onSubmit}
        isSaving={isSaving}
        enableDictation={enableDictation}
        readOnly={readOnly}
      />
    </Dialog>
  );
}
